import React, { useState, useEffect } from 'react'
import { Divider, Typography, Button, message } from 'antd'
import Uploader from '../util/Uploader'
import MappingEditor from '../util/MappingEditor'
import VpsWhitelistEditor from '../util/VpsWhitelistEditor'
import DuplicateCheckEditor from '../util/DuplicateCheckEditor'
import { useSelector, useDispatch } from 'react-redux'
import { createSignUri } from "../../utils/signLink"
import { updateDocument } from '../../actions/admin'
const { Title, Paragraph } = Typography

const InfoText = ({ title, text, children }) => {
  return (
    <div>
      <Title level={4}>{title}</Title>
      <Paragraph>{text}</Paragraph>
      {children}
    </div>
  )
}

const validateFields = (fields) => {
  const seen = new Set()
  const seenPdf = new Set()
  for (const f of fields) {
    if (!f.key) return 'Every field needs a key'
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(f.key)) return `Invalid key "${f.key}" — start with a letter, use only letters/numbers/underscore`
    if (!f.label || !f.label.trim()) return `Field "${f.key}" needs a label`
    if (seen.has(f.key)) return `Duplicate key "${f.key}"`
    seen.add(f.key)
    if (f.pdfField) {
      if (seenPdf.has(f.pdfField)) return `PDF field "${f.pdfField}" mapped more than once`
      seenPdf.add(f.pdfField)
    }
  }
  return null
}

const Upload = props => {
  const dispatch = useDispatch()
  const dbId = useSelector(state => state.data.mongoDbId)
  const lastUpload = useSelector(state => state.data.lastUpload)
  const signicatUrl = createSignUri(dbId)

  const [fields, setFields] = useState([])
  const [formTitle, setFormTitle] = useState('')
  const [formIntro, setFormIntro] = useState('')
  const [vpsSource, setVpsSource] = useState(null)
  const [vpsWhitelist, setVpsWhitelist] = useState(null)
  const [duplicateCheck, setDuplicateCheck] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (lastUpload) {
      setFields(lastUpload.fields || [])
      setFormTitle(lastUpload.formTitle || '')
      setFormIntro(lastUpload.formIntro || '')
      setVpsSource(lastUpload.vpsSource || null)
      setVpsWhitelist(lastUpload.vpsWhitelist || null)
      setDuplicateCheck(lastUpload.duplicateCheck || null)
      setSaved(false)
    }
  }, [lastUpload])

  const handleVpsSourceChange = (doc) => {
    setVpsSource(doc.vpsSource || null)
    setVpsWhitelist(doc.vpsWhitelist || null)
    setSaved(false)
  }

  const handleMappingChange = (next) => {
    setFields(next.fields)
    setFormTitle(next.formTitle)
    setFormIntro(next.formIntro)
    setSaved(false)
  }

  const saveMapping = async () => {
    if (!dbId) return
    const err = validateFields(fields)
    if (err) {
      message.error(err)
      return
    }
    setSaving(true)
    dispatch(updateDocument(dbId, { fields, formTitle, formIntro, vpsWhitelist, duplicateCheck }))
    setTimeout(() => {
      setSaving(false)
      setSaved(true)
    }, 400)
  }

  return (
    <div className="stdContent">
      <Title level={3}>Upload new offering</Title>
      <Divider />
      <InfoText
        title="1. Create PDF Form"
        text="Follow the steps to create a PDF Form"
      />
      <Divider />
      <InfoText title="2. Upload file">
        <Uploader />
      </InfoText>
      <Divider />
      {dbId && lastUpload && (
        <>
          <InfoText
            title="3. Configure form"
            text="Set the header text shown to the customer, then configure which fields to collect and how they map to the PDF. Unmapped values are stored on the database entry and included in the Excel export."
          >
            <MappingEditor
              pdfFieldNames={lastUpload.pdfFieldNames || []}
              fields={fields}
              formTitle={formTitle}
              formIntro={formIntro}
              onChange={handleMappingChange}
            />
            <Divider />
            <VpsWhitelistEditor
              docId={dbId}
              fields={fields}
              vpsSource={vpsSource}
              vpsWhitelist={vpsWhitelist}
              onSourceChange={handleVpsSourceChange}
              onChange={(wl) => { setVpsWhitelist(wl); setSaved(false) }}
            />
            <Divider />
            <DuplicateCheckEditor
              fields={fields}
              value={duplicateCheck}
              defaultVpsFieldKey={vpsWhitelist && vpsWhitelist.fieldKey}
              onChange={(dc) => { setDuplicateCheck(dc); setSaved(false) }}
            />
            <Button
              type="primary"
              onClick={saveMapping}
              loading={saving}
              style={{ marginTop: 16 }}
            >
              {saved ? 'Saved' : 'Save form configuration'}
            </Button>
          </InfoText>
          <Divider />
        </>
      )}
      <InfoText
        title={dbId && lastUpload ? "4. Copy this link" : "3. Copy this link"}
        text={fields.length > 0
          ? "Customers opening this link will be shown the form above before Signicat:"
          : "This PDF has no fillable fields, so customers opening this link go straight to Signicat. Add form fields above if you want to collect details first:"}
      >
        {dbId ? (
          <Paragraph copyable={{tooltips: ["Copy link", "Link copied!"]}}>
            {signicatUrl}
          </Paragraph>
        ) : (
          <Paragraph italic>
            Upload a file to generate link
          </Paragraph>
        )}
      </InfoText>
    </div>
  )
}

export default Upload
